import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarBlank, Folder } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { MaskedLine, Reveal } from "../components/Kinetic";
import { TWO_A_URL, EvalNote } from "../components/EstimateCTA";
import { Seo, SITE, SITE_NAME } from "../components/Seo";
import DOMPurify from "dompurify";
import { api, imgSrc } from "../lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/articles/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-24 text-center" data-testid="article-not-found">
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">Guide not found</h1>
        <p className="mt-4 text-[#4A4A4A]">This guide may have moved. Browse all Buyback Guides instead.</p>
        <Link to="/guides" className="mt-8 rounded-full bg-navy px-8 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white hover:bg-brand" data-testid="not-found-guides-link">
          All Guides
        </Link>
      </main>
    );
  }

  if (!data) {
    return <main className="min-h-[70vh] pt-24" data-testid="article-loading"><div className="mx-auto mt-20 h-96 max-w-3xl animate-pulse rounded-3xl bg-[#F5F5F7]" /></main>;
  }

  const { article, related } = data;
  const path = `/guides/${article.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.meta_description,
      image: article.image_url?.startsWith("/") ? `${SITE}${article.image_url}` : article.image_url,
      datePublished: article.published_at,
      dateModified: article.updated_at,
      author: { "@type": "Organization", name: SITE_NAME, url: SITE },
      publisher: { "@type": "Organization", name: SITE_NAME, url: SITE },
      mainEntityOfPage: `${SITE}${path}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Buyback Guides", item: `${SITE}/guides` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${SITE}${path}` },
      ],
    },
  ];

  if (article.faqs?.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <main data-testid="article-page">
      <Seo title={article.meta_title} description={article.meta_description} path={path} type="article" jsonLd={jsonLd} />

      <article className="pt-32 md:pt-40">
        <div className="mx-auto max-w-3xl px-6">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#8A8A8A]" data-testid="article-breadcrumbs">
            <Link to="/" className="transition-colors hover:text-brand">Home</Link>
            <span>/</span>
            <Link to="/guides" className="transition-colors hover:text-brand">Guides</Link>
            <span>/</span>
            <span className="text-navy">{article.category}</span>
          </nav>
          <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl">
            <MaskedLine delay={0.1}>{article.title}</MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <div className="mt-6 flex flex-wrap items-center gap-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8A8A]">
              <span className="flex items-center gap-2"><Folder size={15} weight="duotone" className="text-brand" />{article.category}</span>
              <span className="flex items-center gap-2"><CalendarBlank size={15} weight="duotone" className="text-brand" />{fmt(article.published_at)}</span>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="spotlight-frame mt-10 overflow-hidden rounded-3xl">
              <img src={imgSrc(article.image_url)} alt={article.image_alt} className="max-h-[440px] w-full object-cover" loading="eager" data-testid="article-hero-image" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="article-body mx-auto mt-12 max-w-3xl px-6" data-testid="article-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content_html, { ALLOWED_TAGS: ["p", "h2", "h3", "ul", "ol", "li", "strong", "a", "em", "br"], ALLOWED_ATTR: ["href"] }) }} />
        </Reveal>

        {article.faqs?.length > 0 && (
          <div className="mx-auto mt-14 max-w-3xl px-6" data-testid="article-faqs">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Common questions</h2>
              <Accordion type="single" collapsible className="mt-6">
                {article.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-black/10">
                    <AccordionTrigger className="text-left font-display text-base font-semibold text-navy hover:text-brand" data-testid={`faq-trigger-${i}`}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-[#4A4A4A]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        )}

        <div className="mx-auto mt-16 max-w-3xl px-6">
          <Reveal>
            <div className="rounded-3xl bg-navy p-9 text-center md:p-12" data-testid="article-cta">
              <h2 className="font-display text-2xl font-extrabold tracking-tighter text-white md:text-3xl">
                Have a working device to sell?
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/70">
                Get an indicative range in under a minute — final offer after a free in-person evaluation in Chicago.
              </p>
              <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid="article-estimate-btn"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white hover:text-navy">
                Get a Buyback Estimate
                <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <div className="mt-4 flex justify-center"><EvalNote light /></div>
            </div>
          </Reveal>
        </div>

        {related?.length > 0 && (
          <div className="mx-auto mt-20 max-w-7xl px-6 pb-24 md:px-12" data-testid="related-articles">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Keep reading</h2>
            </Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.07}>
                  <Link to={`/guides/${r.slug}`} data-testid={`related-${r.slug}`}
                    className="group block overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                    <div className="h-44 overflow-hidden">
                      <img src={imgSrc(r.image_url)} alt={r.image_alt} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{r.category}</p>
                      <h3 className="mt-2 flex items-start justify-between gap-3 font-display text-base font-bold leading-snug tracking-tight text-black group-hover:text-brand">
                        {r.title}
                        <ArrowUpRight size={16} weight="bold" className="mt-0.5 shrink-0" />
                      </h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </article>
    </main>
  );
}
